import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import multiphotos from 'domains/multiphotos';
import crops from 'domains/crops';

import { useAppDispatch, useAppSelector } from 'utils/hooks';

import UserImage from './UserImage';

import type { PhotoUpload } from 'domains/photos';
import type { PhotoSortIndicator } from './Filmstrip';

type UserPhotoThumbProps = {
  id?: 'first-photo-thumb';
  photo: PhotoUpload;
  canUseMultiPhotos: boolean;
  activeSortIndicator: PhotoSortIndicator | null;
};

function UserPhotoThumb(props: UserPhotoThumbProps) {
  const dispatch = useAppDispatch();
  const selectedPhotoIds = useAppSelector(multiphotos.selectors.selectedPhotoIds);
  const multiphotoIds = useAppSelector(multiphotos.selectors.selectIds) as number[];
  const allCrops = useAppSelector(crops.selectors.selectAll);
  const potentialCropIds = useAppSelector(crops.selectors.potentialCropIds);

  const { photo, id, canUseMultiPhotos, activeSortIndicator } = props;

  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);

  const selected = useMemo(
    () => selectedPhotoIds.indexOf(photo.id) !== -1,
    [selectedPhotoIds, photo]
  );

  // which indicator is visible if this thumbnail is indicated by Filmstrip as the drag target for sorting
  const indicateSort = useMemo(() => {
    if (activeSortIndicator === null) return false;

    const [indicatorId, indicatorPosition] = activeSortIndicator.split('-');

    if (parseInt(indicatorId) !== photo.id) return false;

    return indicatorPosition;
  }, [activeSortIndicator, photo]);

  const style: React.CSSProperties = useMemo(() => {
    if (imageLoaded) {
      return {};
    }

    return { backgroundImage: "url('/assets/loading.gif')" };
  }, [imageLoaded]);

  function handleMultiSelect(e: React.ChangeEvent<HTMLInputElement>) {
    e.stopPropagation();

    if (!canUseMultiPhotos) {
      if (selected) {
        dispatch(crops.actions.deleteCropsByPhotoId(photo.id));
      } else {
        dispatch(crops.actions.applyPhotoToActiveNode(photo.id));
      }

      // Early return if we're not dealing with multiphotos
      return;
    }

    // If we got to here, there should only be one canvas
    const cropsCount = allCrops.length;
    const multiphotosCount = multiphotoIds.length;

    if (cropsCount === 0 && multiphotosCount === 0) {
      dispatch(crops.actions.applyPhotoToActiveNode(photo.id));
    }

    if (cropsCount > 0) {
      if (selected) {
        dispatch(crops.actions.deleteCropsByPhotoId(photo.id));
      } else {
        // pull all photos from crops and put into multiphotos
        dispatch(multiphotos.actions.moveCropsToMultiPhotos([photo.id]));
      }
    }

    if (multiphotosCount > 0) {
      if (selected) {
        // remove from multiphotos
        // if multiphotoscount is 2 (meaning this will leave one after removal), move remaining photo to active photos
        const remaining = multiphotoIds.filter((pid) => pid !== photo.id);
        if (multiphotosCount === 2) {
          const remainingPhotoId = remaining[0];
          dispatch(crops.actions.applyPhotoToActiveNode(remainingPhotoId));
          dispatch(multiphotos.actions.resetMultiPhotos());
        } else {
          dispatch(multiphotos.actions.deleteMultiPhoto(photo.id));
        }
      } else {
        // add to multiphotos
        dispatch(multiphotos.actions.createMultiPhotoCrop(photo.id));
      }
    }
  }

  function handlePhotoClick(p: PhotoUpload) {
    if (potentialCropIds.length === 1) {
      dispatch(multiphotos.actions.resetMultiPhotos());
    }

    dispatch(crops.actions.applyPhotoToActiveNode(p.id));
  }

  if (imageLoadError) {
    return (
      <li key={photo.id} title="Image failed to load">
        <div className="image-load-error-icon">
          <FontAwesomeIcon icon="exclamation-circle" size="2x" />
        </div>
      </li>
    );
  }

  return (
    <li
      key={photo.id}
      id={id && id}
      data-id={photo.id}
      data-sort={photo.sort_index}
      className={`user_photo_thumb thumb_container ${selected ? 'active' : ''}`}
      style={style}
    >
      <label className="multi-select-container">
        <span className="screen-reader-text">
          {selected && 'de-'}select photo {photo.original_filename}
        </span>
        <input
          type="checkbox"
          className="screen-reader-text"
          checked={selected}
          onChange={handleMultiSelect}
        />
        <span className="fa-layers fa-fw multi-select-trigger">
          <FontAwesomeIcon icon={selected ? ['fas', 'square'] : ['far', 'square']} size="lg" />
          {selected && <FontAwesomeIcon icon="check" />}
        </span>
      </label>
      <div
        className={`sort-indicator left ${indicateSort && indicateSort === 'left' ? 'active' : ''}`}
        aria-hidden
      />
      <button onClick={() => handlePhotoClick(photo)}>
        <div className="thumb_util">
          <div className="thumb_filename" title={photo.original_filename}>
            <span className="filename">{photo.original_filename}</span>
          </div>
        </div>
        <div className="thumb_img_wrap">
          <UserImage
            setImageLoaded={() => setImageLoaded(true)}
            setImageLoadError={setImageLoadError}
            id={photo.id}
            path={photo.file_path_thumb}
            alt={photo.original_filename}
          />
        </div>
      </button>
      <div
        className={`sort-indicator right ${
          indicateSort && indicateSort === 'right' ? 'active' : ''
        }`}
        aria-hidden
      />
    </li>
  );
}

export default UserPhotoThumb;
